/**
 * Rich text helpers shared by questions, answers and anything else stored as HTML.
 *
 *   <RichHtml html={question.question_text} />      sanitised render (plain text is rendered as-is, with line breaks)
 *   stripHtml(html)  → plain text (for grid cells, search, titles)
 *   isHtml(value)    → true when the string contains markup
 */
import React from 'react';
import DOMPurify from 'dompurify';

const ALLOWED = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'span', 'a', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
    'h1', 'h2', 'h3', 'h4', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'hr'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height', 'class', 'style', 'colspan', 'rowspan'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

export function isHtml(value) {
  return typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);
}

export function stripHtml(value) {
  if (!value) return '';
  if (!isHtml(value)) return String(value);
  const div = document.createElement('div');
  div.innerHTML = DOMPurify.sanitize(value, ALLOWED);
  const imgs = div.querySelectorAll('img').length;
  const text = (div.textContent || '').replace(/\s+/g, ' ').trim();
  return imgs && !text ? '[figure]' : text;
}

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function toHtml(value) {
  if (!value) return '';
  if (isHtml(value)) return value;
  return value.split(/\n{2,}/).map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br/>')}</p>`).join('');
}

/**
 * A table or a code block cannot wrap — it is as wide as its widest row. On a phone that
 * used to make the whole lesson draggable sideways, which is why every paragraph was
 * clipped at both edges. Give those two their own scroll box instead, so the wide thing
 * scrolls and the text around it stays still. Everything else is capped by CSS.
 */
function boxWideBlocks(root) {
  root.querySelectorAll('table, pre').forEach((el) => {
    if (el.parentElement?.classList?.contains('rich-scroll')) return;
    if (el.closest('.rich-scroll')) return;          // a table inside a boxed table
    const box = root.ownerDocument.createElement('div');
    box.className = 'rich-scroll';
    el.parentNode.insertBefore(box, el);
    box.appendChild(el);
  });
}

export function RichHtml({ html, className = '', as: Tag = 'div', ...rest }) {
  const clean = React.useMemo(() => {
    const safe = DOMPurify.sanitize(toHtml(html), { ...ALLOWED, ADD_ATTR: ['target'] });
    if (typeof document === 'undefined') return safe;
    const holder = document.createElement('div');
    holder.innerHTML = safe;
    boxWideBlocks(holder);
    return holder.innerHTML;
  }, [html]);
  return <Tag className={`rich-text ${className}`} dangerouslySetInnerHTML={{ __html: clean }} {...rest} />;
}

export default RichHtml;
